import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, X, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const STORAGE_KEY = 'apisai.login_prompt_dismissed';

/**
 * LoginPromptModal — zachęta do logowania po pierwszym ❤️ Save.
 *
 * Logika retencji:
 *  - Gdy niezalogowany user klika ❤️ na karcie, zapis działa anonimowo (localStorage + X-Client-Id).
 *  - Po krótkiej chwili pokazujemy delikatny modal: „Zapisz ogłoszenie i wróć do niego później".
 *  - User może odrzucić — zapis zostaje lokalny, modal nie pokaże się ponownie w tej sesji.
 *  - Brak twardego logowania, brak SaaS-owego dashboardu — lekkie, naturalne.
 */
export default function LoginPromptModal({ open, onClose, context = 'favorite' }) {
  const [shake, setShake] = useState(false);

  useEffect(() => {
    if (open) setShake(false);
  }, [open]);

  if (!open) return null;

  const handleDismiss = () => {
    try { sessionStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignore */ }
    onClose?.();
  };

  const isFavorite = context === 'favorite';
  const title = isFavorite
    ? 'Zapisz ogłoszenie i wróć do niego później'
    : 'Zapisz wyszukiwanie i wróć później';
  const sub = isFavorite
    ? 'Zaloguj się, by mieć ulubione ogłoszenia na każdym urządzeniu i nie stracić ich z oczu.'
    : 'Zaloguj się, by dostawać powiadomienia o nowych ogłoszeniach pasujących do Twoich filtrów.';

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end md:items-center justify-center bg-black/65 backdrop-blur-sm p-0 md:p-4"
      onClick={handleDismiss}
      data-testid="login-prompt-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`w-full md:max-w-md rounded-t-3xl md:rounded-3xl border border-white/10 bg-zinc-950 p-6 shadow-2xl text-white ${shake ? 'animate-pulse' : ''}`}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-full flex items-center justify-center ${isFavorite ? 'bg-pink-500/15 text-pink-300' : 'bg-orange-500/15 text-orange-300'}`}>
              <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-white/45">
                {isFavorite ? '❤️ Zapisano' : '🔔 Zapisano'}
              </div>
              <div className="text-base font-semibold leading-tight mt-0.5">{title}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            data-testid="login-prompt-close"
            className="rounded-full p-1.5 text-white/55 hover:bg-white/10 hover:text-white transition shrink-0"
            aria-label="Zamknij"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="text-sm text-white/60 mb-5">{sub}</p>

        <div className="flex flex-col gap-2">
          <Link to="/logowanie" data-testid="login-prompt-cta" className="w-full">
            <Button className="w-full rounded-xl bg-orange-500 hover:bg-orange-400 text-black font-semibold h-11">
              Zaloguj się
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </Link>
          <button
            type="button"
            onClick={handleDismiss}
            data-testid="login-prompt-skip"
            className="text-xs text-white/45 hover:text-white/70 transition py-2"
          >
            Może później
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Helper: czy pokazać prompt po działaniu typu favorite/save_search.
 * - Niezalogowany user
 * - Modal nie był jeszcze pokazany w tej sesji
 */
export function shouldShowLoginPrompt() {
  try {
    const isLoggedIn = !!localStorage.getItem('token');
    if (isLoggedIn) return false;
    if (sessionStorage.getItem(STORAGE_KEY) === '1') return false;
    return true;
  } catch {
    return false;
  }
}
